// src/components/Admin/ActivityLogs.js — embedded in the Admin dashboard (no outer padding).
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Box, Typography, TextField, MenuItem, Select, Button,
  FormControl, InputLabel, CircularProgress, IconButton, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import styled, { keyframes } from 'styled-components';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SettingsIcon from '@mui/icons-material/Settings';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PersonIcon from '@mui/icons-material/Person';
import TimelineIcon from '@mui/icons-material/Timeline';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon         from '@mui/icons-material/Delete';
import UndoIcon           from '@mui/icons-material/Undo';
import AutorenewIcon      from '@mui/icons-material/Autorenew';
import AddIcon            from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  FONT_BODY, FONT_DISPLAY, COLORS, TYPE, RADIUS, SHADOW, CONTROL_HEIGHT, ELLIPSIS_CSS,
} from '../../styles/recruiterTheme';

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Styled Components ────────────────────────────────────────────────────────
const Wrapper = styled.div`
  font-family: ${FONT_BODY};
  animation: ${fadeIn} 0.35s ease both;
`;

const PageTitle = styled(Typography)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.page}px !important;
    line-height: 1.25 !important;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #2D1B5E 0%, #6750A4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0 !important;
  }
`;

const PageSubtitle = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.body}px !important;
    line-height: 1.5 !important;
    color: ${COLORS.muted} !important;
    font-weight: 500 !important;
    margin: 4px 0 0 !important;
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: ${p => p.gradient};
  border-radius: ${RADIUS.md}px;
  padding: 16px 24px;
  min-height: 96px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  color: #fff;
  box-shadow: 0 8px 24px ${p => p.shadow || 'rgba(0,0,0,0.12)'};
  position: relative;
  overflow: hidden;
  &::after {
    content: '';
    position: absolute;
    top: -30px; right: -30px;
    width: 90px; height: 90px;
    border-radius: 50%;
    background: rgba(255,255,255,0.1);
  }
`;

const StatNum = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.stat}px;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
`;

const StatLbl = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1.25;
  opacity: 0.88;
  text-transform: uppercase;
`;

const Card = styled.div`
  background: ${COLORS.surface};
  border: 1px solid ${COLORS.border};
  border-radius: ${RADIUS.lg}px;
  box-shadow: ${SHADOW.card};
  overflow: hidden;
  min-width: 0;
`;

const ControlBar = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid ${COLORS.border};
`;

const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const HeadCell = styled(TableCell)`
  && {
    background: linear-gradient(135deg, #4F378B 0%, #6750A4 100%);
    color: #fff !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.overline}px !important;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 12px 16px !important;
    white-space: nowrap;
    vertical-align: middle;
    border-bottom: none !important;
  }
`;

const BodyRow = styled(TableRow)`
  && {
    transition: background 0.16s;
    &:nth-child(even) td { background: #FDFBFF; }
    &:hover td { background: ${COLORS.background} !important; }
    &:last-child td { border-bottom: none !important; }
  }
`;

const BodyCell = styled(TableCell)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.small}px !important;
    font-weight: 500 !important;
    color: ${COLORS.ink} !important;
    padding: 12px 16px !important;
    vertical-align: middle;
    border-bottom: 1px solid #F0EAF9 !important;
  }
`;

const CellEllipsis = styled.div`
  ${ELLIPSIS_CSS}
  max-width: ${p => p.maxWidth || 220}px;
`;

const ActionIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: ${RADIUS.sm}px;
  background: ${p => p.bg || COLORS.border};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  svg { font-size: 16px; color: ${p => p.color || COLORS.primary}; }
`;

const RoleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 12px;
  border-radius: ${RADIUS.pill}px;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  line-height: 1.5;
  background: ${p => p.bg};
  color: ${p => p.fg};
  white-space: nowrap;
`;

const GhostButton = styled(Button)`
  && {
    color: ${COLORS.primary} !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 600 !important;
    font-size: ${TYPE.small}px !important;
    text-transform: none !important;
    border-radius: ${RADIUS.sm}px !important;
    height: ${CONTROL_HEIGHT.md}px;
    padding: 0 16px !important;
    border: 1px solid rgba(103,80,164,0.35) !important;
    &:hover { background: rgba(103,80,164,0.08) !important; }
  }
`;

const controlSx = {
  '& .MuiOutlinedInput-root': { borderRadius: `${RADIUS.sm}px`, height: CONTROL_HEIGHT.md, background: COLORS.surface, fontSize: TYPE.small },
};
const navSx = {
  width: 32, height: 32, borderRadius: `${RADIUS.sm}px`,
  border: `1px solid ${COLORS.border}`, color: COLORS.primary,
  '&:hover': { background: COLORS.surfaceTint },
  '&.Mui-disabled': { color: COLORS.faint, borderColor: COLORS.border },
};

const pad2 = (n) => String(n).padStart(2, '0');
// "29 Oct 2026 · 11:00"; em dash when missing/unparseable.
const formatDateTime = (raw) => {
  const d = new Date(raw);
  if (raw == null || raw === '' || Number.isNaN(d.getTime())) return '—';
  const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  return `${date} · ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

// ─── Config maps ──────────────────────────────────────────────────────────────
const ACTION_CONFIG = {
    CREATE_JD:                          { label: 'Created JD',                 icon: <CheckCircleIcon />,  bg: '#E8F5E9', color: '#2E7D32' },
  UPDATE_JD:                          { label: 'Updated JD',                 icon: <EditIcon />,         bg: '#FFF8E1', color: '#F57F17' },
  ARCHIVE_JD:                         { label: 'Archived JD',                icon: <SettingsIcon />,     bg: '#EDE7F6', color: '#6750A4' },
  UNARCHIVE_JD:                       { label: 'Unarchived JD',              icon: <CheckCircleIcon />,  bg: '#E8F5E9', color: '#2E7D32' },
  SEARCH_BY_DOMAIN:                   { label: 'Searched by domain',        icon: <SearchIcon />,       bg: '#E3F2FD', color: '#1565C0' },
  UPLOAD_RESUMES:                     { label: 'Uploaded resumes',          icon: <FileDownloadIcon />, bg: '#E8F5E9', color: '#2E7D32' },
  SCHEDULE_INTERVIEW_CONFIRMED:       { label: 'Interview confirmed',       icon: <CheckCircleIcon />,  bg: '#E8F5E9', color: '#2E7D32' },
  ADD_INTERVIEWER_MANUALLY:           { label: 'Added interviewer manually', icon: <PersonIcon />,      bg: '#EDE7F6', color: '#6750A4' },
  MARK_RESUME_ACTIVE:                 { label: 'Marked resume active',      icon: <CheckCircleIcon />,  bg: '#E8F5E9', color: '#2E7D32' },
MARK_RESUME_INACTIVE:               { label: 'Marked resume inactive',    icon: <CancelIcon />,       bg: '#FDECEA', color: '#B3261E' },
  CANDIDATE_STATUS_CHANGED:     { label: 'Changed status',          icon: <CheckCircleIcon />,  bg: '#E8F5E9', color: '#2E7D32' },
  EDIT_CANDIDATE:               { label: 'Edited candidate',        icon: <EditIcon />,         bg: '#FFF8E1', color: '#F57F17' },
  SEND_SCREENING_TEST:          { label: 'Sent screening test',     icon: <SendIcon />,         bg: '#EDE7F6', color: '#6750A4' },
  RESEND_SCREENING_TEST:        { label: 'Resent screening test',   icon: <SendIcon />,         bg: '#FFF3E0', color: '#E65100' },
  SCHEDULE_INTERVIEW_INITIATED: { label: 'Scheduled interview',     icon: <ScheduleIcon />,     bg: '#E3F2FD', color: '#1565C0' },
  MARK_CANDIDATE_ACTIVE:        { label: 'Marked active',           icon: <CheckCircleIcon />,  bg: '#E8F5E9', color: '#2E7D32' },
  MARK_CANDIDATE_INACTIVE:      { label: 'Marked inactive',         icon: <CancelIcon />,       bg: '#FDECEA', color: '#B3261E' },
  PROVIDE_FEEDBACK_INITIATED:   { label: 'Provided feedback',       icon: <PersonIcon />,       bg: '#EDE7F6', color: '#6750A4' },
  EXPORT_EXCEL:                 { label: 'Exported to Excel',       icon: <FileDownloadIcon />, bg: '#E8F5E9', color: '#2E7D32' },
  CONFIGURE_SCREENING:          { label: 'Configured screening',    icon: <SettingsIcon />,     bg: '#FFF3E0', color: '#E65100' },
  REMOVE_QUESTIONS:       { label: 'Removed questions',        icon: <DeleteIcon />,        bg: '#FDECEA', color: '#B3261E' },
  RESTORE_QUESTION:       { label: 'Restored question',        icon: <UndoIcon />,          bg: '#E8F5E9', color: '#2E7D32' },
  REGENERATE_QUESTIONS:   { label: 'Regenerated questions',    icon: <AutorenewIcon />,     bg: '#EDE7F6', color: '#6750A4' },
  ADD_MANUAL_QUESTION:    { label: 'Added manual question',    icon: <AddIcon />,           bg: '#E3F2FD', color: '#1565C0' },
  DOWNLOAD_QUESTIONS_PDF: { label: 'Downloaded questions PDF', icon: <FileDownloadIcon />,  bg: '#E8F5E9', color: '#2E7D32' },
  DOWNLOAD_REMOVAL_LOG:   { label: 'Downloaded removal log',   icon: <FileDownloadIcon />,  bg: '#FFF3E0', color: '#E65100' },
  VIEW_QUESTIONS:         { label: 'Viewed questions',         icon: <VisibilityIcon />,    bg: '#F3EEF9', color: '#4F378B' },
};

const ROLE_CONFIG = {
  recruiter:     { bg: '#EDE7F6', fg: '#4F378B', label: ' Recruiter' },
  hiringManager: { bg: '#E3F2FD', fg: '#1565C0', label: ' HM' },
  interviewer:   { bg: '#E8F5E9', fg: '#2E7D32', label: ' Interviewer' },
  admin:         { bg: '#FFF3E0', fg: '#E65100', label: ' Admin' },
  system:        { bg: '#F5F5F5', fg: '#616161', label: ' System' },
};


const DEFAULT_ACTION = { icon: <VisibilityIcon />, bg: '#EDE7F6', color: '#6750A4' };

// ─── Main Component ───────────────────────────────────────────────────────────
const ActivityLogs = () => {
  const [logs, setLogs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [search, setSearch]       = useState('');
  const [roleFilter, setRoleFilter]   = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [dateFrom, setDateFrom]   = useState('');
  const [dateTo, setDateTo]       = useState('');
  const [page, setPage]           = useState(1);
  const PER_PAGE = 20;
  const requestIdRef = useRef(0);

  const fetchLogs = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (roleFilter !== 'all')   params.role   = roleFilter;
      if (actionFilter !== 'all') params.action = actionFilter;
      if (dateFrom) params.from = dateFrom;
      if (dateTo)   params.to   = dateTo;
      const res = await axios.get(process.env.REACT_APP_GET_ACTIVITY_LOGS_API, { params });
      if (requestId !== requestIdRef.current) return; // a newer filter request superseded this one
      setLogs(Array.isArray(res.data?.logs) ? res.data.logs : []);
      setPage(1);
    } catch (e) {
      if (requestId !== requestIdRef.current) return;
      console.error('Failed to fetch activity logs:', e);
      setError('Could not load activity logs.');
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [roleFilter, actionFilter, dateFrom, dateTo]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // ── Client-side search filter ─────────────────────────────────────────────
  const filtered = logs.filter(log => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      String(log.performedBy ?? '').toLowerCase().includes(q) ||
      String(log.targetName ?? '').toLowerCase().includes(q)  ||
      String(log.action ?? '').toLowerCase().includes(q) ||
      String(ACTION_CONFIG[log.action]?.label ?? '').toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(Math.ceil(filtered.length / PER_PAGE), 1);
  const paginated  = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  // ── Stats ─────────────────────────────────────────────────────────────────
  const stats = {
    total:     logs.length,
    recruiter: logs.filter(l => l.role === 'recruiter').length,
    hm:        logs.filter(l => l.role === 'hiringManager').length,
    interviewer: logs.filter(l => l.role === 'interviewer').length,
  };

  // Known actions plus any unknown ones seen in the data, so the filter can
  // switch straight from one action to another.
  const actionOptions = [...new Set([...Object.keys(ACTION_CONFIG), ...logs.map(l => l.action).filter(Boolean)])]
    .sort((a, b) => (ACTION_CONFIG[a]?.label || a).localeCompare(ACTION_CONFIG[b]?.label || b));

  const hasFilters = Boolean(search.trim() || roleFilter !== 'all' || actionFilter !== 'all' || dateFrom || dateTo);

  const stateBlock = (content) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, px: 3, gap: 1.5, textAlign: 'center' }}>
      {content}
    </Box>
  );

  return (
    <Wrapper>
      {/* ── Page header ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        {/* <Box sx={{ minWidth: 0 }}>
          <PageTitle>Activity Logs</PageTitle>
          <PageSubtitle>Complete audit trail of user actions across the platform</PageSubtitle>
        </Box> */}
        <Tooltip title="Refresh logs">
          <span>
            <IconButton onClick={fetchLogs} disabled={loading} aria-label="Refresh logs"
              sx={{ width: CONTROL_HEIGHT.md, height: CONTROL_HEIGHT.md, background: COLORS.border, borderRadius: `${RADIUS.sm}px`, '&:hover': { background: '#D0BCFF' } }}>
              <RefreshIcon sx={{ color: COLORS.primary, fontSize: 20 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* ── Stat Cards ── */}
      <StatsRow>
        <StatCard gradient="linear-gradient(135deg,#6750A4,#4F378B)" shadow="rgba(103,80,164,0.28)">
          <StatNum>{stats.total}</StatNum>
          <StatLbl>Total Actions</StatLbl>
        </StatCard>
        <StatCard gradient="linear-gradient(135deg,#1565C0,#0D47A1)" shadow="rgba(21,101,192,0.26)">
          <StatNum>{stats.recruiter}</StatNum>
          <StatLbl>By Recruiters</StatLbl>
        </StatCard>
        <StatCard gradient="linear-gradient(135deg,#2E7D32,#1B5E20)" shadow="rgba(46,125,50,0.26)">
          <StatNum>{stats.hm}</StatNum>
          <StatLbl>By Hiring Managers</StatLbl>
        </StatCard>
        <StatCard gradient="linear-gradient(135deg,#E65100,#BF360C)" shadow="rgba(230,81,0,0.26)">
          <StatNum>{stats.interviewer}</StatNum>
          <StatLbl>By Interviewers</StatLbl>
        </StatCard>
      </StatsRow>

      <Card>
        {/* ── Controls ── */}
        <ControlBar>
          <TextField
            size="small"
            placeholder="Search by user, candidate, action…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            sx={{ ...controlSx, width: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: COLORS.primary }} />
                </InputAdornment>
              )
            }}
          />

          <FormControl size="small" sx={{ minWidth: 160, ...controlSx }}>
            <InputLabel>Role</InputLabel>
            <Select value={roleFilter} label="Role" onChange={e => setRoleFilter(e.target.value)}>
              <MenuItem value="all">All roles</MenuItem>
              <MenuItem value="recruiter">Recruiter</MenuItem>
              <MenuItem value="hiringManager">Hiring Manager</MenuItem>
              <MenuItem value="interviewer">Interviewer</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="system">System</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200, ...controlSx }}>
            <InputLabel>Action</InputLabel>
            <Select value={actionFilter} label="Action" onChange={e => setActionFilter(e.target.value)}
              MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}>
              <MenuItem value="all">All actions</MenuItem>
              {actionOptions.map(a => (
                <MenuItem key={a} value={a}>{ACTION_CONFIG[a]?.label || a}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
            value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            inputProps={{ max: dateTo || undefined }}
            sx={{ ...controlSx, width: 160 }} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
            value={dateTo} onChange={e => setDateTo(e.target.value)}
            inputProps={{ min: dateFrom || undefined }}
            sx={{ ...controlSx, width: 160 }} />

          {filtered.length !== logs.length && (
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, fontWeight: 700, color: COLORS.primaryDark,
              background: COLORS.border, borderRadius: `${RADIUS.sm}px`, px: 1.5, py: 0.5, fontVariantNumeric: 'tabular-nums' }}>
              {filtered.length} result{filtered.length !== 1 ? 's' : ''}
            </Typography>
          )}
        </ControlBar>

        {/* ── Log table ── */}
        {loading ? stateBlock(
          <>
            <CircularProgress size={28} sx={{ color: COLORS.primary }} />
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.body, color: COLORS.muted }}>Loading activity logs…</Typography>
          </>
        ) : error ? stateBlock(
          <>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.body, color: COLORS.error }}>{error}</Typography>
            <GhostButton onClick={fetchLogs}>Retry</GhostButton>
          </>
        ) : paginated.length === 0 ? stateBlock(
          <>
            <TimelineIcon sx={{ fontSize: 48, color: '#D0BCFF' }} />
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.cardTitle, fontWeight: 700, color: COLORS.muted }}>
              No activity logs found
            </Typography>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.small, color: COLORS.faint }}>
              {hasFilters ? 'Try adjusting your filters' : 'Actions will appear here as users interact with the platform'}
            </Typography>
          </>
        ) : (
          <>
            <TableScroll>
              <Table sx={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                <TableHead>
                  <TableRow>
                    <HeadCell>Action</HeadCell>
                    <HeadCell>Performed by</HeadCell>
                    <HeadCell>Role</HeadCell>
                    <HeadCell>Timestamp</HeadCell>
                    <HeadCell>Target / note</HeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.map((log, i) => {
                    const cfg  = ACTION_CONFIG[log.action] || { ...DEFAULT_ACTION, label: log.action || '—' };
                    const role = ROLE_CONFIG[log.role]     || { bg: '#F5F5F5', fg: '#616161', label: log.role || '—' };
                    return (
                      <BodyRow key={log._id || `${log.timestamp}-${i}`}>
                        <BodyCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                            <ActionIcon bg={cfg.bg} color={cfg.color}>{cfg.icon}</ActionIcon>
                            <Box sx={{ minWidth: 0 }}>
                              <CellEllipsis title={cfg.label} style={{ fontWeight: 700, color: COLORS.primaryDeep }}>{cfg.label}</CellEllipsis>
                              {log.metadata?.action && (
                                <CellEllipsis title={String(log.metadata.action)} style={{ fontSize: TYPE.caption, color: COLORS.muted }}>
                                  action: {String(log.metadata.action)}
                                </CellEllipsis>
                              )}
                            </Box>
                          </Box>
                        </BodyCell>
                        <BodyCell>
                          <CellEllipsis maxWidth={200} title={log.performedBy || ''} style={{ fontWeight: 600 }}>
                            {log.performedBy || '—'}
                          </CellEllipsis>
                        </BodyCell>
                        <BodyCell sx={{ whiteSpace: 'nowrap' }}>
                          <RoleBadge bg={role.bg} fg={role.fg}>{String(role.label).trim()}</RoleBadge>
                        </BodyCell>
                        <BodyCell sx={{ whiteSpace: 'nowrap', color: `${COLORS.muted} !important`, fontVariantNumeric: 'tabular-nums' }}>
                          {formatDateTime(log.timestamp)}
                        </BodyCell>
                        <BodyCell>
                          {log.targetName && (
                            <CellEllipsis maxWidth={260} title={String(log.targetName)} style={{ fontWeight: 600, color: COLORS.primaryDark }}>
                              {String(log.targetName)}
                            </CellEllipsis>
                          )}
                          {log.metadata?.note && (
                            <CellEllipsis maxWidth={260} title={String(log.metadata.note)} style={{ fontSize: TYPE.caption, color: COLORS.muted, fontStyle: 'italic' }}>
                              {String(log.metadata.note)}
                            </CellEllipsis>
                          )}
                          {log.jdId && (
                            <CellEllipsis maxWidth={260} title={String(log.jdId)} style={{ fontSize: TYPE.caption, color: COLORS.faint }}>
                              JD: {String(log.jdId)}
                            </CellEllipsis>
                          )}
                          {!log.targetName && !log.metadata?.note && !log.jdId && '—'}
                        </BodyCell>
                      </BodyRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableScroll>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2,
                px: 2, py: 1.5, borderTop: `1px solid ${COLORS.border}` }}>
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.small, color: COLORS.muted, fontVariantNumeric: 'tabular-nums' }}>
                  Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, filtered.length)} of {filtered.length}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Tooltip title="Previous page">
                    <span>
                      <IconButton size="small" sx={navSx} onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={page === 1} aria-label="Previous page">
                        <ChevronLeftIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.small, color: COLORS.ink, fontWeight: 600, minWidth: 80, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                    Page {page} of {totalPages}
                  </Typography>
                  <Tooltip title="Next page">
                    <span>
                      <IconButton size="small" sx={navSx} onClick={() => setPage(p => Math.min(p + 1, totalPages))} disabled={page === totalPages} aria-label="Next page">
                        <ChevronRightIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            )}
          </>
        )}
      </Card>
    </Wrapper>
  );
};

export default ActivityLogs;
